import * as React from 'react';
import { ExternalLink, Send } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';

export interface UserPreviewUser {
  id: number;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  email: string | null;
  telegramId?: number | null;
  /** Имя колонки из схемы БД User.photoUrl (единый контракт с backend preview). */
  photoUrl?: string | null;
}

export interface UserPreviewCellProps {
  user: UserPreviewUser;
  className?: string;
}

function getDisplayName(user: UserPreviewUser): string {
  const parts = [user.firstName, user.lastName].filter(Boolean);
  if (parts.length > 0) {
    return parts.join(' ');
  }
  if (user.username) {
    return `@${user.username}`;
  }
  if (user.email) {
    return user.email;
  }
  return `#${user.id}`;
}

function getAvatarFallback(user: UserPreviewUser): string {
  if (user.firstName) {
    return user.firstName.charAt(0).toUpperCase();
  }
  if (user.username) {
    return user.username.charAt(0).toUpperCase();
  }
  if (user.email) {
    return user.email.charAt(0).toUpperCase();
  }
  return '#';
}

/**
 * Переиспользуемая ячейка пользователя: имя+фамилия с hover-карточкой.
 * При наведении показывает аватар, контакты и ссылку на профиль.
 * Чисто presentational — без data-fetch.
 */
export function UserPreviewCell({ user, className }: UserPreviewCellProps) {
  const displayName = getDisplayName(user);
  const fallback = getAvatarFallback(user);
  const profileUrl = `/users/show/${user.id}`;

  return (
    <HoverCard>
      <HoverCardTrigger asChild>
        <span
          className={cn(
            'cursor-default truncate text-sm font-medium hover:text-primary transition-colors',
            className,
          )}
        >
          {displayName}
        </span>
      </HoverCardTrigger>
      <HoverCardContent className="w-72" align="start">
        <div className="flex flex-col gap-3">
          <div className="flex items-start gap-3">
            <Avatar className="size-10 shrink-0">
              {user.photoUrl && (
                <AvatarImage src={user.photoUrl} alt={displayName} />
              )}
              <AvatarFallback className="text-sm font-medium">
                {fallback}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1 space-y-0.5">
              {(user.firstName || user.lastName) && (
                <p className="text-sm font-semibold leading-tight truncate">
                  {[user.firstName, user.lastName].filter(Boolean).join(' ')}
                </p>
              )}
              {user.username && (
                <p className="text-xs text-muted-foreground truncate">
                  @{user.username}
                </p>
              )}
              {user.email && (
                <p className="text-xs text-muted-foreground truncate">
                  {user.email}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {user.telegramId && (
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                <Send className="h-3 w-3" />
                Telegram
              </span>
            )}
            <span className="text-xs text-muted-foreground">
              #{user.id}
            </span>
          </div>

          <Button
            asChild
            variant="outline"
            size="sm"
            className="w-full gap-1.5 text-xs"
          >
            <a href={profileUrl} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-3.5 w-3.5" />
              Открыть профиль
            </a>
          </Button>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
